import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from "lucide-react"
import { cn } from "@/lib/utils"

// Alert color language mirrors Badge: subtle 15% accent-color tint behind
// accent-colored text. No full-fill, no shouting.
const alertVariants = cva(
  "flex items-start gap-3 w-full rounded px-4 py-3 text-sm [&>svg]:h-4 [&>svg]:w-4 [&>svg]:stroke-[1.5] [&>svg]:shrink-0 [&>svg]:mt-0.5",
  {
    variants: {
      variant: {
        default:     "bg-foreground/5 text-foreground",
        destructive: "bg-destructive/15 text-destructive",
        success:     "bg-success/15 text-success",
        warning:     "bg-warning/15 text-warning",
      },
    },
    defaultVariants: { variant: "default" },
  }
)

const variantIcons = {
  default:     Info,
  destructive: AlertCircle,
  success:     CheckCircle2,
  warning:     AlertTriangle,
} as const

type AlertVariant = NonNullable<VariantProps<typeof alertVariants>["variant"]>

type AlertProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof alertVariants> & {
    /** Replace the default variant icon. Pass `null` to opt out entirely. */
    icon?: React.ReactNode
  }

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant, icon, children, ...props }, ref) => {
    const VariantIcon = variantIcons[(variant ?? "default") as AlertVariant]
    const resolvedIcon = icon === undefined ? <VariantIcon aria-hidden="true" /> : icon
    return (
      <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
        {resolvedIcon}
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    )
  }
)
Alert.displayName = "Alert"

const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h5 ref={ref} className={cn("mb-1 text-base font-medium leading-none", className)} {...props} />
  )
)
AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("text-sm", className)} {...props} />
  )
)
AlertDescription.displayName = "AlertDescription"

export { Alert, AlertTitle, AlertDescription }
